import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { useAssetGenerator } from '@/hooks/activation';

interface PackActivateInAppSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

export const PackActivateInAppSection = ({
  pack,
  brand = '#4E9CFF',
}: PackActivateInAppSectionProps) => {
  const { downloadByType } = useAssetGenerator(pack);
  const [isDownloading, setIsDownloading] = useState(false);

  const name = ActivationPackService.getTargetName(pack);
  const smartLink = ActivationPackService.getAppSmartLinkUrl(pack);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadByType('app_poster_png');
      toast.success('Poster downloaded');
    } catch {
      toast.error("Couldn't build the poster");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <OverviewSectionCard
      title="Activate in app"
      subtitle="One QR to put anywhere. Scanning it takes people straight to your Genie from their phone."
      genieIcon="qr-code"
      brand={brand}
    >
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Live, on-brand QR */}
        <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shrink-0">
          <div className="rounded-[14px] bg-white p-5 flex items-center justify-center">
            <QRCodeSVG value={smartLink} size={180} level="H" fgColor="#222B45" />
          </div>
        </div>

        <div className="text-center sm:text-left">
          <div className="text-base font-semibold text-gray-900">Scan to meet {name}</div>
          <p className="text-sm text-gray-600 mt-1 max-w-sm">
            Phones open the app or jump straight into the conversation. Print it on stickers,
            counter cards, and signage.
          </p>
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            className="mt-4 text-white border-0"
            style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Building…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download poster
              </>
            )}
          </Button>
        </div>
      </div>
    </OverviewSectionCard>
  );
};
